/**
 * Entrance animations — the SITE-level group inside the Buttons & Effects
 * drawer (EA-4).
 *
 * Three controls, in the order the questions arrive:
 *
 *   1. **Template scroll reveal** — only rendered when the project's own markup
 *      actually carries one, named from the registry rather than described in
 *      the abstract. Its toggle is the "keep theirs" switch.
 *   2. **Swap** — the primary action when a template convention IS present.
 *      ONE transaction sets `entrance.template = false` and
 *      `entrance.preset = <chosen>`, because the two are a single decision:
 *      layering both engines on one section is not a smaller version of the
 *      feature, it is a section that fades in twice.
 *   3. **Site entrance** — the preset picker and the stagger checkbox.
 *
 * Everything renders FROM `ENTRANCE_PRESETS`, so a preset cannot exist in the
 * registry and be missing here, or vice versa.
 */

import { eventBus } from '../../core/events.js';
import { store } from '../../core/state.js';
import { createCheckbox, createButton } from '../components.js';
import { bridge } from '../../editing/bridge.js';
import { renderSegmented } from '../design-widgets.js';
import {
  ENTRANCE_PRESETS,
  ENTRANCE_NONE,
  ENTRANCE_DEFAULTS
} from '../../shared/entrance-presets.js';
import {
  detectTemplateRevealConventions,
  stateEmitsEntrance
} from '../../export/scroll-animation-shim.js';

/** Human names for the registry ids the drawer shows the user. */
const CONVENTION_LABELS = Object.freeze({
  aos: 'AOS',
  'reveal-class': 'reveal classes',
  'scroll-trigger': 'scroll-trigger',
  'animated-delay': 'animated + delay'
});

function conventionName(id) {
  return CONVENTION_LABELS[id] || id;
}

/** "AOS", "AOS and reveal classes", "AOS, reveal classes and scroll-trigger". */
export function describeConventions(patternIds) {
  const names = (patternIds || []).map(conventionName);
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function currentEntrance() {
  const raw = store.getState()?.entrance;
  if (!raw || typeof raw !== 'object') return { ...ENTRANCE_DEFAULTS };
  return { ...ENTRANCE_DEFAULTS, ...raw };
}

/**
 * The single write path for a site-level entrance change. A render is requested
 * because the preset is stamped into section MARKUP, not just styled — the same
 * reason `applySectionOverride` requests one for `revealPreset` (D9).
 */
function writeEntrance(path, value, label) {
  store.update(`entrance.${path}`, value, label);
  eventBus.emit('preview:render-request', { preserveScroll: true });
}

/**
 * Swap: disable the template's convention and adopt a builder preset as ONE
 * history entry. Two `store.update` calls would give the user two undos for one
 * decision, and would leave a middle state — template off, preset still none —
 * in which the page has NO entrance at all.
 */
export function swapToBuilderPreset(presetId) {
  const preset = ENTRANCE_PRESETS.some((p) => p.id === presetId)
    ? presetId
    : ENTRANCE_PRESETS[0].id;
  store.batch(
    [
      { path: 'entrance.template', value: false },
      { path: 'entrance.preset', value: preset }
    ],
    'Swap entrance animation'
  );
  eventBus.emit('preview:render-request', { preserveScroll: true });
  return preset;
}

/**
 * @param {HTMLElement} scope container owned by this group
 * @returns {() => void} teardown
 */
export function renderEntranceGroup(scope) {
  if (!scope) return () => {};

  const build = () => {
    scope.replaceChildren();
    const entrance = currentEntrance();
    const detection = detectTemplateRevealConventions(store.getState());

    const heading = document.createElement('div');
    heading.className = 'pb-props-label pb-entrance-heading';
    heading.textContent = 'Entrance animations';
    scope.appendChild(heading);

    // ── the template's own convention, if it has one ───────────────────────
    if (detection.patternIds.length > 0) {
      const block = document.createElement('div');
      block.className = 'pb-entrance-template-block';

      const detected = document.createElement('p');
      detected.className = 'pb-feature-desc';
      detected.textContent =
        `Template scroll reveal: ${describeConventions(detection.patternIds)} ` +
        `(detected on ${detection.sectionCount} section${detection.sectionCount === 1 ? '' : 's'}).`;
      block.appendChild(detected);

      const keep = createCheckbox({
        label: "Keep the template's scroll reveal",
        checked: entrance.template !== false,
        // `createCheckbox` hands the change EVENT to onChange, not a boolean —
        // taking the argument as truthy would have made this toggle one-way.
        onChange: (ev) => {
          writeEntrance('template', !!ev.target.checked, 'Toggle template scroll reveal');
        }
      });
      block.appendChild(keep.wrap);

      const note = document.createElement('p');
      note.className = 'pb-feature-desc';
      note.textContent =
        'One engine per section: while this is on, a section that already animates itself ' +
        'keeps its own animation and ignores the preset below.';
      block.appendChild(note);

      if (entrance.template !== false) {
        block.appendChild(
          createButton({
            variant: 'primary',
            label: 'Swap for a builder animation',
            onClick: () => {
              swapToBuilderPreset(
                entrance.preset === ENTRANCE_NONE ? ENTRANCE_PRESETS[0].id : entrance.preset
              );
            }
          })
        );
      }
      scope.appendChild(block);
    }

    // ── the builder's own preset ───────────────────────────────────────────
    const presetRow = document.createElement('div');
    presetRow.className = 'pb-override-row pb-entrance-preset-row';
    const presetLabel = document.createElement('label');
    presetLabel.className = 'pb-props-label';
    presetLabel.textContent = 'Site entrance';
    presetRow.appendChild(presetLabel);

    const segment = renderSegmented({
      field: 'entrancePreset',
      ariaLabel: 'Site entrance animation',
      // Rendered FROM the records, plus the `none` sentinel.
      options: [
        { label: 'None', value: ENTRANCE_NONE },
        ...ENTRANCE_PRESETS.map((preset) => ({ label: preset.label, value: preset.id }))
      ],
      value: entrance.preset || ENTRANCE_NONE,
      onChange: (nextValue) => {
        // `renderSegmented` clears an ACTIVE value to '' on a second click.
        // For this control there is no "unset": clearing means None.
        writeEntrance('preset', nextValue || ENTRANCE_NONE, 'Change site entrance');
      }
    });
    presetRow.appendChild(segment);
    scope.appendChild(presetRow);

    const stagger = createCheckbox({
      label: 'Stagger children',
      checked: entrance.stagger !== false,
      disabled: (entrance.preset || ENTRANCE_NONE) === ENTRANCE_NONE,
      onChange: (ev) => {
        writeEntrance('stagger', !!ev.target.checked, 'Toggle entrance stagger');
      }
    });
    scope.appendChild(stagger.wrap);

    // EA-10: a one-shot page-scope replay ON the still canvas — the exception
    // that ends in the canvas's own static state (contracts §5).
    scope.appendChild(
      createButton({
        label: 'Preview entrances',
        variant: 'ghost',
        // Site "none" can still leave section- or element-mode participants.
        // Ask the same effective resolver that decides whether exports ship
        // the runtime, including template-owned suppression.
        disabled: !stateEmitsEntrance(store.getState()),
        onClick: () => bridge.previewEntrance({})
      })
    );

    const scopeNote = document.createElement('p');
    scopeNote.className = 'pb-feature-desc';
    scopeNote.textContent =
      'Plays in Preview and on your exported site. The editing canvas stays still.';
    scope.appendChild(scopeNote);
  };

  build();
  // Rebuilt rather than patched: the template block appears and disappears with
  // the toggle, and the stagger checkbox's disabled state follows the preset.
  const off = eventBus.on('state-changed', build);
  return () => off?.();
}
